import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { IModalHandle, Modal } from "../modal/Modal";
import styles from "./PortalBlast.module.scss";
import { Button } from "../button/Button";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { getMandatoryNotifications } from "@src/store/notifications/getMandatoryNotificationsSlice";
import { getMandatoryNotificationReadIds, TERMS_AND_CONDITIONS_TYPE_ID } from "@src/store/notifications/mandatoryNotification";
import { updateNotificationReadStatus } from "@src/store/notifications/updateNotificationReadStatusSlice";
import ShadowHTML from "../shadowHTML/ShadowHTML";

const SUBMIT_ERROR_MESSAGE = "We could not save your acknowledgement. Please try again.";

const PortalBlast = () => {
	const [index, setIndex] = useState(0);
	const [isAcknowledged, setIsAcknowledged] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const data = useAppSelector((state) => state.notification.mandatoryNotifications.data);

	const currentNotification = data[index];
	const maxIndex = data.length - 1;
	const remainingCount = Math.max(data.length - index, 0);

	const isTermsAndConditions = currentNotification?.Type_ID === TERMS_AND_CONDITIONS_TYPE_ID;
	const mustAccept = currentNotification?.Must_Accept ?? false;
	const canSubmit = !mustAccept || isAcknowledged;

	const modalRef = useRef<IModalHandle>(null);
	const dispatch = useAppDispatch();

	const handleButtonClick = async () => {
		if (!currentNotification || isSubmitting) return;

		const notificationIds = getMandatoryNotificationReadIds(currentNotification);

		if (notificationIds.length > 0) {
			setIsSubmitting(true);
			setSubmitError(null);

			try {
				await dispatch(updateNotificationReadStatus({ NotificationIds: notificationIds, Read: true })).unwrap();
			} catch (error) {
				setSubmitError(typeof error === "string" && error ? error : SUBMIT_ERROR_MESSAGE);
				setIsSubmitting(false);
				return;
			}

			setIsSubmitting(false);
		}

		goToNext();
	};

	const goToNext = () => {
		setSubmitError(null);
		setIsAcknowledged(false);

		if (index >= maxIndex) modalRef.current?.closeModal();
		else setIndex((prev) => prev + 1);
	};

	const handleClose = () => {
		if (isSubmitting) return;
		goToNext();
	};

	const handleAcknowledge = () => setIsAcknowledged((prev) => !prev);

	useEffect(() => {
		const handleNotifications = async () => {
			try {
				const innerData = await dispatch(getMandatoryNotifications({ skipCache: true })).unwrap();
				if (innerData.length <= 0) return;

				modalRef.current?.openModal();
			} catch {
				return;
			}
		};

		handleNotifications();
	}, [dispatch]);

	return (
		<Modal ref={modalRef}>
			<div className={styles.root}>
				<div className={styles.header}>
					<span>{remainingCount} New Notification(s) Need Your Attention</span>
					{!mustAccept ? (
						<button type="button" className={styles.close} aria-label="Close" onClick={handleClose}>
							<X size={18} />
						</button>
					) : null}
				</div>
				<div className={styles.content}>
					<div className={styles.title}>{currentNotification?.Subject}</div>
					<div className={styles.shadowHTML}>
						{isTermsAndConditions ? (
							<>
								{currentNotification?.TermsandConditionsMessage ? (
									<ShadowHTML html={currentNotification.TermsandConditionsMessage} />
								) : null}
								{currentNotification?.PrivacyPolicyMessage ? (
									<ShadowHTML html={currentNotification.PrivacyPolicyMessage} />
								) : null}
							</>
						) : (
							<ShadowHTML html={currentNotification?.Message} />
						)}
					</div>

					{submitError ? <div className={styles.error}>{submitError}</div> : null}

					<div className={styles.cta}>
						{mustAccept ? (
							<div className={styles.acknowledgement}>
								<input
									type="checkbox"
									id="acknowledged-inp"
									checked={isAcknowledged}
									disabled={isSubmitting}
									onChange={handleAcknowledge}
								/>
								<label className={styles.label} htmlFor="acknowledged-inp">
									{isTermsAndConditions
										? "I have read and accept the Terms & Conditions and Privacy Policy"
										: "I have read the above notification"}
								</label>
							</div>
						) : null}
						<Button disabled={!canSubmit} isLoading={isSubmitting} onClick={handleButtonClick}>
							Ok, Got It
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default PortalBlast;
